import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatChipsModule} from '@angular/material/chips';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {DeviceType} from '@birco/rainmesh-api';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {combineLatest, defer, map, Observable, startWith} from 'rxjs';
import {InputWrapperComponent, isSubmitAllowed} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {TagComponent} from '../../../../shared/components/tag/tag.component';
import {DeviceActions} from '../../../devices/state/device.actions';
import {selectDevicesByType} from '../../../devices/state/device.selectors';
import {DeviceResponseDto} from '../../../devices/models/device-response.interface';

export type ConnectedValvesDialogData = {
  pump: DeviceResponseDto;
  connectedValveIds: string[];
};

export type ConnectedValvesDialogResult = {
  pumpId: string;
  connectedValveIds: string[];
};

// defer, so the initial value is read on subscribe rather than captured at field initialization.
const controlValue = <T>(control: AbstractControl, fallback: T): Observable<T> => defer(
  () => control.valueChanges.pipe(startWith(control.value)) as Observable<T | null | undefined>,
).pipe(map((value) => value ?? fallback));

@Component({
  selector: 'app-dialog-update-connected-valves',
  imports: [
    AsyncPipe,
    MatDialogModule,
    TranslatePipe,
    ReactiveFormsModule,
    InputWrapperComponent,
    MatAutocompleteModule,
    MatChipsModule,
    IconComponent,
    TagComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dialog-update-connected-valves.component.html',
  styleUrl: './dialog-update-connected-valves.component.scss',
})
export class DialogUpdateConnectedValvesComponent {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);

  data = inject<ConnectedValvesDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DialogUpdateConnectedValvesComponent>);

  // No required validator: an empty selection is a valid full disconnect.
  readonly form: FormGroup = this.fb.group({
    connectedValveIds: [this.data?.connectedValveIds ?? []],
    valveSearch: [''],
  });

  // Out-of-service valves stay selectable: a temporarily disabled valve still belongs to its
  // pump. They are sorted last and carry a tag instead of being filtered out.
  private readonly selectableValves$ = this.store.select(selectDevicesByType(DeviceType.VALVE)).pipe(
    map((valves) => valves
      .slice()
      .sort((left, right) => {
        if (left.isOutOfService !== right.isOutOfService) {
          return left.isOutOfService ? 1 : -1;
        }
        return left.name.localeCompare(right.name);
      })),
  );

  private readonly selectedIds$ = controlValue<string[]>(this.form.controls['connectedValveIds'], []);

  readonly selectedValves$ = combineLatest([
    this.selectableValves$,
    this.selectedIds$,
  ]).pipe(
    map(([valves, selectedIds]) => {
      const selectedIdSet = new Set(selectedIds);
      return valves.filter((valve) => selectedIdSet.has(valve.id));
    }),
  );

  readonly availableValves$ = combineLatest([
    this.selectableValves$,
    controlValue<string>(this.form.controls['valveSearch'], ''),
    this.selectedIds$,
  ]).pipe(
    map(([valves, search, selectedIds]) => {
      const searchTerm = search.trim().toLowerCase();
      const selectedIdSet = new Set(selectedIds);

      return valves
        .filter((valve) => !selectedIdSet.has(valve.id))
        .filter((valve) => searchTerm.length === 0 || valve.name.toLowerCase().includes(searchTerm));
    }),
  );

  constructor() {
    this.store.dispatch(DeviceActions.public.loadAllDevicesByFilters({
      filter: {deviceTypes: [DeviceType.VALVE]},
    }));
  }

  onSubmit() {
    if (!isSubmitAllowed(this.form)) {
      return;
    }

    this.dialogRef.close({
      pumpId: this.data.pump.id,
      connectedValveIds: (this.form.value.connectedValveIds ?? []) as string[],
    } satisfies ConnectedValvesDialogResult);
  }

  addValve(id: string) {
    const current = (this.form.controls['connectedValveIds'].value ?? []) as string[];
    const next = Array.from(new Set([...current, id]));

    this.form.controls['connectedValveIds'].setValue(next);
    this.form.controls['connectedValveIds'].markAsDirty();
    this.form.controls['connectedValveIds'].updateValueAndValidity();
    this.form.controls['valveSearch'].setValue('');
  }

  removeValve(id: string) {
    const current = (this.form.controls['connectedValveIds'].value ?? []) as string[];
    const next = current.filter((currentId) => currentId !== id);

    this.form.controls['connectedValveIds'].setValue(next);
    this.form.controls['connectedValveIds'].markAsDirty();
    this.form.controls['connectedValveIds'].updateValueAndValidity();
  }
}
